import { Component, OnChanges, OnInit, SimpleChanges, input, output } from '@angular/core';
import { DoodProductModel, IProductLabel } from '@core/models/product.model';
import { ProductService } from '@core/services/product/product.service';
import { ProductKeys } from '@config/keys/product.keys';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { combineLatest } from 'rxjs';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { ProductTrackerService } from '@core/services/app/product-tracker.service';
import { CommonModule } from '@angular/common';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { PriceAtomComponent } from '../price-atom/price-atom.component';
import { VisibleLabelsPipe } from '@shared/pipes/visible-product-labels/visible-product-labels.pipe';
import { TagAtomComponent } from '../tag-atom/tag-atom.component';
import { TranslateModule } from '@ngx-translate/core';
import { DiscountPercentPipe } from '@core/pipes/discount-percent.pipe';

@Component({
  selector: 'app-product-push-v2-atom',
  templateUrl: './product-push-v2-atom.component.html',
  styleUrls: ['./product-push-v2-atom.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ContentBuilderColorPipe,
    PriceAtomComponent,
    VisibleLabelsPipe,
    TagAtomComponent,
    TranslateModule,
    DiscountPercentPipe,
  ],
})
export class ProductPushV2AtomComponent implements OnInit, OnChanges {
  protected readonly defaultProductNameColor: IContentBuilderFieldColor = {
    value: 'neutral-900',
    type: ColorFieldTypesValues.Hex,
  };

  protected readonly defaultProductDescriptionColor: IContentBuilderFieldColor = {
    value: 'neutral-900',
    type: ColorFieldTypesValues.Hex,
  };

  protected readonly defaultProductPriceColor: IContentBuilderFieldColor = {
    value: 'neutral-900',
    type: ColorFieldTypesValues.Hex,
  };

  protected readonly defaultProductImagePlaceholderColor: IContentBuilderFieldColor = {
    value: 'neutral-50',
    type: ColorFieldTypesValues.Hex,
  };

  readonly displayContext = input('');
  readonly product = input.required<DoodProductModel>();
  readonly isOrderingAllowed = input(true);
  readonly productNameColor = input<IContentBuilderFieldColor | undefined>(
    this.defaultProductNameColor,
  );
  readonly productDescriptionColor = input<IContentBuilderFieldColor | undefined>(
    this.defaultProductDescriptionColor,
  );
  readonly productPriceColor = input<IContentBuilderFieldColor | undefined>(
    this.defaultProductPriceColor,
  );
  readonly productImagePlaceholderColor = input<IContentBuilderFieldColor | undefined>(
    this.defaultProductImagePlaceholderColor,
  );
  readonly tagColors = input.required<ContentBuilderFieldColors>();
  readonly buttonClick = output<DoodProductModel>();

  get productLabels(): IProductLabel[] {
    return this.product()[ProductKeys.Labels] ?? [];
  }

  isAvailable!: boolean;

  private readonly filterColors$ =
    this.marketplaceSelector.selectMarketPlaceProductLabelsFilterColors;

  protected readonly vm$ = combineLatest({
    filterColors: this.filterColors$,
  });

  constructor(
    private readonly productService: ProductService,
    private readonly marketplaceSelector: MarketplaceStoreSelector,
    private readonly productTrackerService: ProductTrackerService,
  ) {}

  ngOnInit(): void {
    this.productTrackerService.trackProductDisplayed(this.product(), this.displayContext());
  }

  public get ProductKeys(): typeof ProductKeys {
    return ProductKeys;
  }

  goToProduct(product: DoodProductModel): void {
    if (this.isAvailable) {
      this.buttonClick.emit(product);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.product) {
      this.checkIsAvailable();
    }
  }

  checkIsAvailable(): void {
    this.isAvailable = this.productService.isAvailable(this.product());
  }

  displayDiscount(product: DoodProductModel): boolean {
    const discount = product[ProductKeys.Discount];
    return (discount && discount > 0) === true;
  }

  forceType(data: any): any {
    return data as any;
  }
}
